// src/components/JourneyStrip.tsx
import React from 'react';
import { Check, Circle, Cog, Eye } from 'lucide-react';

interface JourneyStripProps {
  currentStatus: string;
  createdBy: string;
  assigneeName?: string;
}

type StepState = 'done' | 'active' | 'pending' | 'cancelled';
type StepTone = 'progress' | 'review' | 'done';

interface Step {
  key: string;
  short: string;
  user?: string;
  state: StepState;
  tone: StepTone;
  icon: React.ReactNode;
}

const TONE_STYLES: Record<StepTone, Record<StepState, { bg: string; border: string; text: string; line: string }>> = {
  /* Progress — blue tones */
  progress: {
    pending:   { bg: '#f1f5f9', border: '#cbd5e1', text: '#94a3b8', line: '#e2e8f0' },
    active:    { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af', line: '#3b82f6' },
    done:      { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af', line: '#3b82f6' },
    cancelled: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c', line: '#ef4444' },
  },
  /* Review — yellow / amber tones (stays yellow even when complete) */
  review: {
    pending:   { bg: '#f1f5f9', border: '#cbd5e1', text: '#94a3b8', line: '#e2e8f0' },
    active:    { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', line: '#f59e0b' },
    done:      { bg: '#fef3c7', border: '#f59e0b', text: '#92400e', line: '#f59e0b' },
    cancelled: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c', line: '#ef4444' },
  },
  /* Complete — green tones */
  done: {
    pending:   { bg: '#f1f5f9', border: '#cbd5e1', text: '#94a3b8', line: '#e2e8f0' },
    active:    { bg: '#d1fae5', border: '#10b981', text: '#047857', line: '#10b981' },
    done:      { bg: '#d1fae5', border: '#10b981', text: '#047857', line: '#10b981' },
    cancelled: { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c', line: '#ef4444' },
  },
};

export function JourneyStrip({ currentStatus, createdBy, assigneeName }: JourneyStripProps) {
  const isDone = currentStatus === 'DONE';
  const isCancelled = currentStatus === 'CANCELLED';
  const inProgress = currentStatus === 'IN_PROGRESS';
  const inReview = currentStatus === 'IN_REVIEW';

  const steps: Step[] = [
    {
      key: 'progress',
      short: 'Progress',
      tone: 'progress',
      state: inProgress ? 'active' : (isDone || inReview) ? 'done' : 'pending',
      icon: <Cog className="w-3 h-3" strokeWidth={2.5} />,
    },
    {
      key: 'review',
      short: 'Review',
      tone: 'review',
      state: inReview ? 'active' : isDone ? 'done' : 'pending',
      icon: <Eye className="w-3 h-3" strokeWidth={2.5} />,
    },
    {
      key: 'done',
      short: 'Complete',
      tone: 'done',
      state: isDone ? 'done' : isCancelled ? 'cancelled' : 'pending',
      icon: <Check className="w-3 h-3" strokeWidth={3} />,
    },
  ];

  return (
    <div className="flex items-start w-full pt-1.5 pb-1">
      {steps.map((step, idx) => {
        const s = TONE_STYLES[step.tone][step.state];
        const isLast = idx === steps.length - 1;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center" style={{ minWidth: 0, flex: '0 0 auto' }}>
              <div
                title={step.user ? `${step.short} · ${step.user}` : step.short}
                style={{
                  width: 22, height: 22, borderRadius: 999,
                  background: s.bg,
                  border: `1.5px solid ${s.border}`,
                  color: s.text,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 200ms cubic-bezier(0.33, 1, 0.68, 1)',
                  boxShadow: step.state === 'active' ? '0 0 0 3px rgba(59, 130, 246, 0.18)' : undefined,
                }}
              >
                {step.icon}
              </div>
              <span
                className="gt-eyebrow truncate"
                style={{
                  marginTop: 4, marginBottom: 0,
                  fontSize: 8.5, letterSpacing: '0.04em',
                  color: s.text,
                  maxWidth: 56, textAlign: 'center',
                }}
              >
                {step.short}
              </span>
            </div>
            {!isLast && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: s.line,
                  borderRadius: 999,
                  marginTop: 10,
                  marginLeft: 4,
                  marginRight: 4,
                  transition: 'background 200ms cubic-bezier(0.33, 1, 0.68, 1)',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
